import { DeductionComponentV2, RunPayrollDeductionV2 } from 'components/RunPayroll/utils/types';
import {
  EarningPayTypeWithoutNonPayableBenefits,
  SalaryComponent,
} from 'components/SalaryComponents/types';
import {
  deductFrom,
  earningPayTypes,
  taxDeductionBehavior,
} from 'components/WizardViews/SalaryComponents/constants';
import { generateUserId } from 'utils/commons';
import { rupeeAmountToPaise, uniqueId } from 'utils/Numbers';
import { PayrollAllowanceV2, UpdateAdditionsDeductionsLopPayload } from './types';
import { ExistingSalaryComponents } from 'components/Fnf/types';

export const convertDataToUpdateAdditionsDeductionsPayload = ({
  allowances,
  deductions,
  userId,
  payrollMonth,
  existingComponents,
  isM3S1Enabled = false,
}: {
  allowances: PayrollAllowanceV2[];
  deductions: DeductionComponentV2;
  userId: string;
  payrollMonth: string;
  existingComponents: ExistingSalaryComponents;
  isM3S1Enabled?: boolean;
}): UpdateAdditionsDeductionsLopPayload => {
  const existingAdditions = existingComponents.additions || [];
  const existingDeductions = existingComponents.deductions || [];

  const additionsObject = allowances
    .filter((allowances) => allowances.id !== '')
    .filter((allowances) => parseInt(allowances.amount.toString()) > 0)
    .filter((comp) => !isAdditionAdhocPayment(comp))
    .map((allowances) => {
      const baseObject = {
        componentId: allowances.id || '',
        value: String(rupeeAmountToPaise(allowances.amount)),
        includeInCompensation: false,
        includeInPayroll: true,
      };

      // Conditionally add payType only if M3S1 is enabled
      if (isM3S1Enabled) {
        return {
          ...baseObject,
          payType: allowances?.payType || '',
        };
      }

      return baseObject;
    });

  const deductionsObject = deductions.non_lop_deductions
    .filter((deductions) => deductions.id !== '')
    .filter((deductions) => parseInt(deductions.amount.toString()) !== 0)
    .filter((comp) => !isDeductionAdhocPayment(comp) && !isDeductionRecurring(comp))
    .map((deductions) => {
      return {
        componentId: deductions.id || '',
        value: String(rupeeAmountToPaise(deductions.amount)),
        includeInCompensation: false,
        includeInPayroll: true,
      };
    });

  const deletedAdditionIds = existingAdditions
    .filter((comp) => comp.server_id)
    .filter(
      (comp) =>
        !isAdditionAdhocPayment({
          ...comp,
          payType: '',
          componentDetails: {},
        } as PayrollAllowanceV2),
    )
    .filter((comp) => !additionsObject.some((c) => c.componentId === comp.id))
    .map((comp) => comp.server_id as string);

  const deletedDeductionIds = existingDeductions
    .filter((comp) => comp.server_id)
    .filter(
      (comp) =>
        !isDeductionAdhocPayment(comp as RunPayrollDeductionV2) &&
        !isDeductionRecurring(comp as RunPayrollDeductionV2),
    )
    .filter((comp) => !deductionsObject.some((c) => c.componentId === comp.id))
    .map((comp) => comp.server_id as string);

  return {
    effectiveFrom: payrollMonth.split('-').splice(0, 2).join('-'),
    effectiveTo: payrollMonth.split('-').splice(0, 2).join('-'),
    userId: generateUserId(userId),
    additions: additionsObject,
    deductions: deductionsObject,
    lopData: {
      payrollMonth: payrollMonth,
      data: [
        {
          peopleId: Number(userId),
          lopDays: Number(deductions.lop_deduction.lop_days),
        },
      ],
      source: 'manual',
    },
    deletedAdditionIds,
    deletedDeductionIds,
  };
};

export const getSearchDropdownDescription = (salaryComponent: SalaryComponent) => {
  if (salaryComponent.category === 'EARNINGS_CATEGORY') {
    return `${
      earningPayTypes[
        salaryComponent.settings.earningDetails.payType as EarningPayTypeWithoutNonPayableBenefits
      ].label
    } 
    ${
      salaryComponent.settings.behaviour.taxBehaviour?.taxDeductionBehaviour
        ? `| ${
            taxDeductionBehavior[
              salaryComponent.settings.behaviour.taxBehaviour?.taxDeductionBehaviour
            ]?.label
          }`
        : ''
    }`;
  } else if (salaryComponent.category === 'DEDUCTIONS_CATEGORY') {
    return `Deduct from ${deductFrom[salaryComponent.settings.deductionDetails.deductFrom].label}`;
  }
};

export const filterSalaryComponents = <T extends SalaryComponent>(
  items: T[],
  search: string,
  ignoreKeys: string[] = [],
) => {
  const searchLower = search.toLowerCase();
  return items.filter((item) => {
    const matchesSearch = item.name.toLowerCase().includes(searchLower);
    return matchesSearch && !ignoreKeys.includes(item.id);
  });
};

export const makeArrayOfMinimumLength = <T>(arr: T[], minLength: number, defaultItem: T): T[] => {
  if (arr.length >= minLength) return arr;
  if (arr.length < minLength) {
    const newItems = Array.from({ length: minLength - arr.length }, () => {
      const newItem = { ...defaultItem };
      // Generate unique ID if the item has an id property
      if (typeof newItem === 'object' && newItem !== null && 'id' in newItem) {
        (newItem as any).id = uniqueId();
      }
      return newItem;
    });
    return [...arr, ...newItems];
  }
  return Array.from({ length: minLength }, () => defaultItem);
};

export const isAdditionAdhocPayment = (allowance: PayrollAllowanceV2) => {
  return allowance.additionalSdiData?.addition_type === 'adhoc_payment';
};

export const isDeductionAdhocPayment = (deduction: RunPayrollDeductionV2) => {
  return deduction.additionalSdiData?.deduction_type === 'adhoc_payment';
};

export const isDeductionRecurring = (deduction: RunPayrollDeductionV2) => {
  return deduction.additionalSdiData?.deduction_type === 'recurring';
};

export const getAdditionAdhocPayments = (allowances: PayrollAllowanceV2[]) => {
  return allowances.filter(isAdditionAdhocPayment);
};

export const getDeductionAdhocPayments = (deductions: DeductionComponentV2) => {
  return deductions.non_lop_deductions.filter(isDeductionAdhocPayment);
};

export const getNonLopDeductionAdhocPayments = (deductions: RunPayrollDeductionV2[]) => {
  return deductions.filter(isDeductionAdhocPayment);
};

export const getDeductionRecurring = (deductions: DeductionComponentV2) => {
  return deductions.non_lop_deductions.filter(isDeductionRecurring);
};

export const getFilteredAllowances = (allowances: PayrollAllowanceV2[]) => {
  return allowances.filter((it) => !isAdditionAdhocPayment(it));
};

export const getFilteredDeductions = (deductions: DeductionComponentV2) => {
  return {
    non_lop_deductions: deductions.non_lop_deductions.filter(
      (it) => !isDeductionAdhocPayment(it) && !isDeductionRecurring(it),
    ),
    lop_deduction: deductions.lop_deduction,
    total_deduction: deductions.total_deduction,
  };
};

export const getFilteredNonLopDeductions = (nonLopDeductions: RunPayrollDeductionV2[]) => {
  return nonLopDeductions.filter((it) => !isDeductionAdhocPayment(it));
};

export const getFilteredRecurringDeductions = (deductions: RunPayrollDeductionV2[]) => {
  return deductions.filter((it) => !isDeductionRecurring(it));
};
